import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, History, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { api, apiError } from "../lib/api";
import { EDITABLE, fmtDate } from "../lib/model";
import { STEPS } from "../lib/constants";
import { AppHeader, AppFooter, NewInspectionButton } from "../components/layout/Layout";
import { ActionButton } from "../components/common/Field";
import { StepNav } from "../components/wizard/StepNav";
import { HistoryDialog } from "../components/common/HistoryDialog";
import Step1Vehicle from "../components/steps/Step1Vehicle";
import Step2Reference from "../components/steps/Step2Reference";
import Step3Measurements from "../components/steps/Step3Measurements";
import Step4Map from "../components/steps/Step4Map";
import Step5Anomalies from "../components/steps/Step5Anomalies";
import Step6Photos from "../components/steps/Step6Photos";
import Step7Analysis from "../components/steps/Step7Analysis";
import Step8Laudo from "../components/steps/Step8Laudo";
import Step9Signature from "../components/steps/Step9Signature";

const VIEWS = { 1: Step1Vehicle, 2: Step2Reference, 3: Step3Measurements, 4: Step4Map, 5: Step5Anomalies, 6: Step6Photos, 7: Step7Analysis, 8: Step8Laudo, 9: Step9Signature };

export default function InspectionEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [insp, setInsp] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [step, setStep] = useState(1);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const version = useRef(0);
  const inspRef = useRef(null);
  inspRef.current = insp;

  useEffect(() => {
    setInsp(null);
    setStep(1);
    Promise.all([api.get(`/inspections/${id}`), api.get(`/inspections/${id}/photos`)])
      .then(([a, b]) => { setInsp(a.data); setPhotos(b.data); setDirty(false); })
      .catch((e) => { toast.error(apiError(e)); nav("/"); });
  }, [id, nav]);

  const update = useCallback((key, value) => {
    version.current += 1;
    setDirty(true);
    setInsp((prev) => ({ ...prev, [key]: typeof value === "function" ? value(prev[key]) : value }));
  }, []);

  const save = useCallback(async (silent = false, patch = {}) => {
    const v = version.current;
    setSaving(true);
    try {
      const payload = { ...Object.fromEntries(EDITABLE.map((k) => [k, inspRef.current[k]])), ...patch };
      const { data } = await api.put(`/inspections/${id}`, payload);
      setInsp((prev) => ({ ...prev, ...patch, updated_at: data.updated_at }));
      if (version.current === v) setDirty(false);
      if (!silent) toast.success("Inspeção salva com sucesso");
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [id]);

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); save(); } };
    const onUnload = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onUnload);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("beforeunload", onUnload); };
  }, [save, dirty]);

  const goStep = async (n) => {
    if (n < 1 || n > 9) return;
    if (dirty) await save(true);
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!insp) {
    return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 border-2 border-[#FFD500] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const View = VIEWS[step];
  const v = insp.vehicle || {};
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader>
        <button type="button" onClick={() => nav("/")} data-testid="back-to-dashboard" className="hidden sm:flex h-9 w-9 items-center justify-center rounded-sm border border-[#1E2F45] text-slate-300 hover:text-white"><ArrowLeft size={16} /></button>
        <div className="hidden md:flex flex-col leading-tight min-w-0 px-2">
          <span className="font-mono font-bold text-[#FFD500] truncate" data-testid="header-placa">{v.placa || "SEM PLACA"}</span>
          <span className="font-mono text-[10px] text-slate-500 truncate">OS {v.numero_ordem_servico || "—"} · {dirty ? <span className="text-amber-400">ALTERAÇÕES NÃO SALVAS</span> : `SALVO ${fmtDate(insp.updated_at)}`}</span>
        </div>
        <div className="flex-1" />
        <ActionButton variant="outline" icon={History} onClick={() => setHistoryOpen(true)} testId="btn-historico" className="hidden lg:inline-flex">Histórico</ActionButton>
        <NewInspectionButton className="hidden xl:inline-flex" />
        <ActionButton icon={Save} onClick={() => save()} disabled={saving} testId="btn-salvar">{saving ? "Salvando" : "Salvar"}</ActionButton>
      </AppHeader>
      <div className="flex-1 grid lg:grid-cols-[270px_1fr]">
        <StepNav step={step} onStep={goStep} insp={insp} photos={photos} />
        <main className="min-w-0 px-4 sm:px-6 lg:px-8 py-8 tactical-grid">
          <View key={step} insp={insp} update={update} save={save} photos={photos} setPhotos={setPhotos} goStep={goStep} />
          <div className="mt-10 flex items-center justify-between border-t border-[#1E2F45] pt-6">
            <ActionButton variant="outline" icon={ChevronLeft} onClick={() => goStep(step - 1)} disabled={step === 1} testId="wizard-prev-button">Anterior</ActionButton>
            <span className="font-mono text-xs text-slate-500 hidden sm:block">{STEPS[step - 1].title.toUpperCase()}</span>
            <ActionButton onClick={() => goStep(step + 1)} disabled={step === 9} testId="wizard-next-button">Próxima <ChevronRight size={16} /></ActionButton>
          </div>
        </main>
      </div>
      <AppFooter />
      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} inspectionId={id} />
    </div>
  );
}
